/**
 * walkClaimsText — Citation Traceability CT6.
 *
 * Renders a narrative string with both:
 *   1. The existing `walkClaimText` pipeline (RefLink + ReliabilityBadge)
 *   2. CT6 per-sentence `<ClaimMarker>` superscript markers
 *
 * Strategy: split the input prose on sentence-ending punctuation,
 * render each sentence through `walkClaimText` (preserving the inline
 * [Ref N] + reliability behaviour), then append a `<ClaimMarker>` when
 * the report's `claims[]` carries a matching entry.
 *
 * Match strategy: substring match on the claim's `text` against each
 * sentence. The LLM is instructed (buildClaimsRules) to emit each
 * claim's text VERBATIM from the narrative, so exact-match is the
 * expected path. Falling back to "normalised substring" handles the
 * (rare) case where the model trims whitespace or rewrites
 * punctuation slightly. Claims that never match drop silently — the
 * `claims[]` field still surfaces in CT9's admin dashboard for QA.
 *
 * Back-compat: when `claims` is undefined/empty, the function output
 * is BYTE-IDENTICAL to `walkClaimText(text, refs)`. Legacy reports
 * render unchanged. The density KPI's prose-fallback path handles
 * those reports separately.
 *
 * Performance: O(sentences × claims). Both are bounded by report
 * size (~hundreds of sentences, ~hundreds of claims) — well within
 * the budget for a single render. The match map is built once per
 * call to avoid quadratic re-scans across long narratives.
 */
import React from 'react';
import type { DataSource } from '../types/siteRag';
import type { ReportClaim } from '../types/citation';
import { walkClaimText } from './walkClaimText';
import { splitSentences } from './citationDensity';
import { ClaimMarker } from '../components/siterag/report/ClaimMarker';
import { sentenceHasInlineRefTokens } from './citationTokens';
import { parseClaimSource } from '../types/citation';
// QA-W7 #3 — the CT6 normalised-match key is now shared with the
// persist-time claims ghost-drop (`reconcileClaimsAgainstNarrative` in
// renumberReferences.ts) so the renderer matcher and the persist
// reconciliation can never drift apart.
import { normaliseClaimTextForMatch } from './renumberReferences';

/**
 * Build a sentence-text → claim-index map for fast lookup. Claims are
 * indexed by their position in the array (1-based) so the marker
 * numbering matches the report-wide ordering.
 *
 * Both keys and lookup text are normalised: whitespace collapsed,
 * surrounding punctuation softened, lower-cased. This handles the
 * (rare) case where the model's `claim.text` differs from the
 * sentence in the narrative by a trailing period or leading capital.
 */
function buildClaimIndex(claims: ReportClaim[]): Map<string, number> {
  const map = new Map<string, number>();
  claims.forEach((c, i) => {
    const key = normaliseForMatch(c.text);
    if (key) {
      // First occurrence wins — the marker is rendered once per match,
      // not per claim. (If a sentence appears twice verbatim only the
      // first instance gets the [¹] marker, which is the desired UX:
      // the second appearance is a paraphrase / restatement.)
      if (!map.has(key)) map.set(key, i + 1);
    }
  });
  return map;
}

/** Shared CT6 matcher — re-exported for callers that previously imported
 *  the local helper. Canonical implementation lives in
 *  `renumberReferences.normaliseClaimTextForMatch`. */
const normaliseForMatch = normaliseClaimTextForMatch;
export { normaliseClaimTextForMatch };

/**
 * N1.5C — `[Ref N] [N]` double-token dedup (DISPLAY ONLY — never touches
 * citation RESOLUTION). The CT6 superscript `[¹]` ClaimMarker is REDUNDANT when
 * the same evidence is ALREADY shown as an inline `[Ref N]` RefLink pill in the
 * surrounding prose: the reader saw "…firm [Ref 1] [¹]" — two affordances for
 * one source. The pre-existing per-sentence guard (`sentenceHasInlineRefTokens`)
 * misses the case where `splitSentences` puts the `[Ref N]` and the matched
 * claim text in DIFFERENT fragments (a `[Ref 1].` token ends a sentence). This
 * block-level guard catches it: collect EVERY `[Ref N]` number across the FULL
 * prose, then suppress the marker when the claim's sources are ENTIRELY `ref:N`
 * tokens already shown inline. A claim with ANY non-ref source (doc / sed /
 * provider / …) keeps its marker — the superscript is then the ONLY affordance
 * for that lineage, so nothing is lost and no citation is reindexed/dropped.
 */
function inlineRefNumbers(text: string): Set<number> {
  const out = new Set<number>();
  for (const g of text.matchAll(/\[Ref\s+[\d\s,]*(?:Ref\s+[\d\s,]*)*\]/gi)) {
    for (const m of g[0].matchAll(/\d+/g)) out.add(Number(m[0]));
  }
  return out;
}

/** True when every source the claim carries is a `ref:N` whose `[Ref N]` is
 *  already shown inline in the prose (so the superscript marker is redundant). */
function claimSourcesAllShownInline(sources: string[] | undefined, inlineRefs: Set<number>): boolean {
  if (!sources || sources.length === 0) return false;
  for (const raw of sources) {
    const parsed = parseClaimSource(raw);
    if (!parsed || parsed.prefix !== 'ref:') return false; // any non-ref source ⇒ keep the marker
    const n = Number(parsed.payload);
    if (!Number.isFinite(n) || !inlineRefs.has(n)) return false; // a ref NOT shown inline ⇒ keep
  }
  return true;
}

export function walkClaimsText(
  text: string | undefined | null,
  refs: DataSource[] | undefined,
  claims: ReportClaim[] | undefined,
  appendixVisibleIndices?: Set<number> | null,
  // BRIEF-MARKER-1 (William 2026-07-04) — suppress the CT6 per-sentence
  // `[¹]` ClaimMarker superscripts. The cover "IN BRIEF" lede rendered a
  // broken/lone `[¹]` marker on a claim-backed sentence that had no inline
  // `[Ref N]`, reading inconsistently with the body's `[N]` RefLink pills.
  // The cover passes this true so the brief shows RefLink pills ONLY.
  opts?: { suppressClaimMarkers?: boolean },
): React.ReactNode[] {
  // No claims attached → identical output to walkClaimText. This is
  // the back-compat path: legacy reports flow through unchanged.
  if (!claims || claims.length === 0) {
    return walkClaimText(text, refs, appendixVisibleIndices);
  }
  if (!text) return [];

  const claimIndex = buildClaimIndex(claims);
  // N1.5C — every `[Ref N]` shown inline across the WHOLE prose (used by the
  // block-level [Ref N] [N] dedup below; cheap O(text) single pass).
  const inlineRefs = inlineRefNumbers(text);
  const sentences = splitSentences(text);
  // splitSentences drops original spacing/newlines; reconstruct with
  // a single space between fragments to keep visual continuity. This
  // is the same approach `walkClaimText` would produce on a flattened
  // input — the only difference is the per-sentence marker insertion.
  const out: React.ReactNode[] = [];
  sentences.forEach((sentence, idx) => {
    if (idx > 0) out.push(' ');
    // Per-sentence walkClaimText: preserves inline [Ref N] + reliability
    // badges within the sentence.
    const sentenceNodes = walkClaimText(sentence, refs, appendixVisibleIndices);
    out.push(<React.Fragment key={`s-${idx}`}>{sentenceNodes}</React.Fragment>);

    const match = claimIndex.get(normaliseForMatch(sentence));
    if (match != null && !sentenceHasInlineRefTokens(sentence) && !opts?.suppressClaimMarkers) {
      const claim = claims[match - 1]!;
      // N1.5C — block-level [Ref N] [N] dedup: skip the superscript when the
      // claim's sources are entirely refs already shown inline elsewhere in the
      // prose (display dedup only — the inline RefLink remains the single
      // canonical affordance; resolution/numbering untouched).
      if (!claimSourcesAllShownInline(claim.sources, inlineRefs)) {
        out.push(
          <ClaimMarker
            key={`m-${idx}`}
            index={match}
            sources={claim.sources}
            references={refs}
            text={claim.text}
          />,
        );
      }
    }
  });
  return out;
}
